// ============================================================
// TRIAGE API — Document intake, staging, and filing
// ============================================================
//
// Uses figApi.js patterns for SharePoint list CRUD.
// Uses FIGVault for file staging and permanent storage.
// Reads St Thomas shared mailbox via Graph API.
//
import {
  getItems, createItem, updateItem,
  ensureFIGVault, createFIGVaultFolder, uploadToFIGVault,
} from './figApi';

const GRAPH = 'https://graph.microsoft.com/v1.0';
const THOMAS_MAILBOX = 'thomas@defineholy.com';
const STAGING_FOLDER = '_Staging';

// ============================================================
// CHECK THOMAS — Read unread emails from shared mailbox
// ============================================================

export async function checkThomas(token) {
  // 1. Get unread messages from St Thomas shared mailbox
  const res = await fetch(
    `${GRAPH}/users/${THOMAS_MAILBOX}/messages?$filter=isRead eq false&$top=50&$select=id,subject,from,toRecipients,receivedDateTime,hasAttachments,bodyPreview,body&$orderby=receivedDateTime desc`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Failed to read Thomas mailbox (${res.status}): ${errText}`);
  }
  const data = await res.json();
  const messages = data.value || [];

  if (messages.length === 0) return { newItems: 0, items: [] };

  // 2. Load existing triage items to avoid duplicates
  let existingMessageIds = new Set();
  try {
    const existing = await getItems(token, 'TriageQueue');
    existingMessageIds = new Set(
      existing.filter(i => i.EmailMessageId).map(i => i.EmailMessageId)
    );
  } catch { /* list may not exist yet — that's fine */ }

  // 3. Ensure staging folder exists in FIGVault
  await ensureFIGVault(token);
  try {
    await createFIGVaultFolder(token, STAGING_FOLDER);
  } catch { /* folder may already exist */ }

  const staged = [];

  for (const msg of messages) {
    // Skip already-processed messages
    if (existingMessageIds.has(msg.id)) {
      // Mark as read since we already have it
      try {
        await fetch(`${GRAPH}/users/${THOMAS_MAILBOX}/messages/${msg.id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ isRead: true }),
        });
      } catch { /* non-critical */ }
      continue;
    }

    const senderName = msg.from?.emailAddress?.name || '';
    const senderEmail = msg.from?.emailAddress?.address || '';
    const subject = msg.subject || '(no subject)';
    const receivedDate = msg.receivedDateTime || new Date().toISOString();
    const bodyPreview = msg.bodyPreview || '';
    const bodyContent = (msg.body?.content || bodyPreview).substring(0, 10000);

    // Detect forwarded emails from Marc — extract original sender
    let originalSenderName = '';
    let originalSenderEmail = '';
    const marcEmails = ['marc@defineholy.com', 'marc@myfiginsurance.com'];
    const isForward = /^(FW|Fwd)\s*:/i.test(subject) && marcEmails.includes(senderEmail.toLowerCase());
    if (isForward) {
      // Parse "From: Name <email>" or "From: email" from the body
      const bodyText = bodyContent.replace(/<[^>]*>/g, ' ');
      const fromMatch = bodyText.match(/From:\s*([^<\n]+?)\s*<([^>]+)>/i)
        || bodyText.match(/From:\s*([^\n<]+@[^\n\s]+)/i);
      if (fromMatch) {
        if (fromMatch[2]) {
          originalSenderName = fromMatch[1].trim();
          originalSenderEmail = fromMatch[2].trim();
        } else {
          originalSenderEmail = fromMatch[1].trim();
        }
      }
    }

    if (msg.hasAttachments) {
      // Pull attachments
      try {
        const attRes = await fetch(
          `${GRAPH}/users/${THOMAS_MAILBOX}/messages/${msg.id}/attachments`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (attRes.ok) {
          const attData = await attRes.json();
          const attachments = (attData.value || []).filter(
            a => a['@odata.type'] === '#microsoft.graph.fileAttachment' && a.contentBytes
          ).filter(a => {
            // Skip all inline attachments (CID-embedded images in email body)
            if (a.isInline) return false;
            const name = (a.name || '').trim();
            const mime = (a.contentType || '').toLowerCase();
            const size = a.size || 0;
            // Skip images under 50KB — likely signature logos even if not marked inline
            if (size < 51200 && mime.startsWith('image/')) return false;
            // Skip images matching common signature naming patterns
            if (/^image\d+(\[\d+\])?\.(png|jpg|jpeg|gif)$/i.test(name)) return false;
            return true;
          });

          for (const att of attachments) {
            // Convert base64 to blob and upload to FIGVault/_Staging
            const byteChars = atob(att.contentBytes);
            const byteArray = new Uint8Array(byteChars.length);
            for (let i = 0; i < byteChars.length; i++) {
              byteArray[i] = byteChars.charCodeAt(i);
            }
            const blob = new Blob([byteArray], {
              type: att.contentType || 'application/octet-stream',
            });

            // Prefix filename with timestamp to avoid collisions
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const fileName = `${timestamp}_${att.name || 'attachment'}`;
            const stagingPath = `${STAGING_FOLDER}/${fileName}`;

            let fileUrl = '';
            try {
              const uploaded = await uploadToFIGVault(token, stagingPath, blob);
              fileUrl = uploaded.webUrl || '';
            } catch (err) {
              console.warn(`[Triage] Failed to stage file ${att.name}:`, err.message);
              continue;
            }

            // Create TriageQueue record
            const triageItem = await createItem(token, 'TriageQueue', {
              Title: att.name || 'attachment',
              Source: 'email',
              SenderEmail: senderEmail,
              SenderName: senderName,
              EmailSubject: subject,
              ReceivedDate: receivedDate,
              FileUrl: fileUrl,
              StagingPath: stagingPath,
              FileSize: att.size || 0,
              FileMimeType: att.contentType || '',
              EmailBody: bodyPreview.substring(0, 2000),
              EmailMessageId: msg.id,
              Status: 'pending',
              // OriginalSenderName/Email — re-enable after adding SharePoint columns
            });
            staged.push(triageItem);
          }
        }
      } catch (err) {
        console.warn(`[Triage] Failed to process attachments for "${subject}":`, err.message);
      }
    } else {
      // Email without attachments — stage the email itself as a triage item
      const triageItem = await createItem(token, 'TriageQueue', {
        Title: subject,
        Source: 'email',
        SenderEmail: senderEmail,
        SenderName: senderName,
        EmailSubject: subject,
        ReceivedDate: receivedDate,
        FileUrl: '',
        StagingPath: '',
        FileSize: 0,
        FileMimeType: 'message/rfc822',
        EmailBody: bodyPreview.substring(0, 2000),
        EmailMessageId: msg.id,
        Status: 'pending',
        // OriginalSenderName/Email — re-enable after adding SharePoint columns
      });
      staged.push(triageItem);
    }

    // Mark email as read in Thomas
    try {
      await fetch(`${GRAPH}/users/${THOMAS_MAILBOX}/messages/${msg.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isRead: true }),
      });
    } catch { /* non-critical */ }
  }

  return { newItems: staged.length, items: staged };
}

// ============================================================
// STAGE UPLOAD — Stage a locally uploaded file
// ============================================================

export async function stageUpload(token, file, userEmail) {
  await ensureFIGVault(token);
  try {
    await createFIGVaultFolder(token, STAGING_FOLDER);
  } catch { /* may exist */ }

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const fileName = `${timestamp}_${file.name}`;
  const stagingPath = `${STAGING_FOLDER}/${fileName}`;

  const uploaded = await uploadToFIGVault(token, stagingPath, file);

  const triageItem = await createItem(token, 'TriageQueue', {
    Title: file.name,
    Source: 'upload',
    SenderEmail: userEmail,
    SenderName: 'Manual Upload',
    EmailSubject: '',
    ReceivedDate: new Date().toISOString(),
    FileUrl: uploaded.webUrl || '',
    StagingPath: stagingPath,
    FileSize: file.size || 0,
    FileMimeType: file.type || '',
    EmailBody: '',
    EmailMessageId: '',
    Status: 'pending',
  });

  return triageItem;
}

// ============================================================
// FILE TRIAGE ITEM — Move from staging to permanent location
// ============================================================

export async function fileTriageItem(token, triageItem, filing, userEmail) {
  const { side, entityName, docType, policyDocType, lob, lobAbbr, effectiveDate, expirationDate, layer } = filing;

  // Build the destination folder path in FIGVault
  // FIG: Clients/{EntityName}/{DocType}
  // Family: Family/{PersonName}/{DocType}
  let destFolder;
  if (side === 'FIG') {
    const entityFolder = `Clients/${entityName}`;
    destFolder = `${entityFolder}/${docType}`;

    // For policy docs, add LOB subfolder
    if (docType === 'Policies' && lob) {
      destFolder = `${entityFolder}/Policies/${lob}`;
    }
  } else {
    destFolder = `Family/${entityName}/${docType}`;
  }

  // Ensure destination folder exists (create nested)
  const parts = destFolder.split('/');
  let buildPath = '';
  for (const part of parts) {
    buildPath = buildPath ? `${buildPath}/${part}` : part;
    try {
      await createFIGVaultFolder(token, buildPath);
    } catch { /* may exist */ }
  }

  // Move file from staging to destination
  if (triageItem.StagingPath) {
    try {
      const driveId = await ensureFIGVault(token);
      const encodedStagingPath = triageItem.StagingPath;

      // Get the staged file's item ID
      const itemRes = await fetch(
        `${GRAPH}/drives/${driveId}/root:/${encodedStagingPath}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (itemRes.ok) {
        const stagedItem = await itemRes.json();

        // Get destination folder ID
        const destRes = await fetch(
          `${GRAPH}/drives/${driveId}/root:/${destFolder}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (destRes.ok) {
          const destFolderItem = await destRes.json();

          // Build renamed filename with metadata
          const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
          const fmtDate = (dateStr) => {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            if (isNaN(d)) return '';
            return `${MONTHS[d.getMonth()]}${String(d.getFullYear()).slice(-2)}`;
          };

          const originalName = triageItem.Title || stagedItem.name;
          const ext = originalName.includes('.') ? originalName.split('.').pop() : '';
          const rawBase = ext ? originalName.slice(0, -(ext.length + 1)) : originalName;
          const baseName = rawBase.replace(/[^a-zA-Z0-9_-]/g, '').substring(0, 30);
          const cleanEntity = (entityName || '').replace(/[\s,.\-']+/g, '');
          const now = new Date();
          const filingDate = `${MONTHS[now.getMonth()]}${String(now.getFullYear()).slice(-2)}`;

          let renamedFile;
          if (side === 'FIG' && lob && effectiveDate) {
            // Policy doc: Entity-LOBAbbr-EffMMMYY-ExpMMMYY-DocType-Original.ext
            const effTag = fmtDate(effectiveDate);
            const expTag = fmtDate(expirationDate);
            const lobTag = lobAbbr || lob.replace(/\s+/g, '');
            const pdtTag = policyDocType ? `-${policyDocType.replace(/\s+/g, '')}` : '';
            renamedFile = `${cleanEntity}-${lobTag}-${effTag}${expTag ? '-' + expTag : ''}${pdtTag}-${baseName}`;
          } else if (side === 'FIG') {
            // Non-policy FIG doc: Entity-DocType-FilingDateMMMYY-Original.ext
            const docTag = (docType || '').replace(/\s+/g, '');
            renamedFile = `${cleanEntity}-${docTag}-${filingDate}-${baseName}`;
          } else {
            // Family: Person-DocType-FilingDateMMMYY-Original.ext
            const docTag = (docType || '').replace(/\s+/g, '');
            renamedFile = `${cleanEntity}-${docTag}-${filingDate}-${baseName}`;
          }
          if (ext) renamedFile += `.${ext}`;

          await fetch(
            `${GRAPH}/drives/${driveId}/items/${stagedItem.id}`,
            {
              method: 'PATCH',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                parentReference: { id: destFolderItem.id },
                name: renamedFile,
              }),
            }
          );
        }
      }
    } catch (err) {
      console.warn('[Triage] File move failed (non-critical):', err.message);
    }
  }

  // Update the TriageQueue record
  const filedPath = side === 'FIG'
    ? `FIG > ${entityName} > ${docType}${policyDocType ? ` > ${policyDocType}` : ''}${lob ? ` > ${lob}` : ''}${effectiveDate ? ` > ${effectiveDate}${expirationDate ? ' to ' + expirationDate : ''}` : ''}${layer ? ` > ${layer}` : ''}`
    : `Family > ${entityName} > ${docType}`;

  await updateItem(token, 'TriageQueue', triageItem.id, {
    Status: 'filed',
    FiledTo: filedPath,
    FiledBy: userEmail,
    FiledDate: new Date().toISOString(),
    Side: side,
    EntityName: entityName,
    EntityType: filing.entityType || '',
    DocType: docType,
    PolicyDocType: policyDocType || '',
    LOB: lob || '',
    EffectiveDate: effectiveDate || '',
    ExpirationDate: expirationDate || '',
    Layer: layer || '',
  });

  // Delete from Thomas mailbox if email source
  if (triageItem.Source === 'email' && triageItem.EmailMessageId) {
    try {
      await deleteFromThomas(token, triageItem.EmailMessageId);
    } catch (err) {
      console.warn('[Triage] Failed to delete from Thomas (non-critical):', err.message);
    }
  }

  return { filedTo: filedPath };
}

// ============================================================
// DELETE FROM THOMAS
// ============================================================

async function deleteFromThomas(token, messageId) {
  const res = await fetch(
    `${GRAPH}/users/${THOMAS_MAILBOX}/messages/${messageId}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok && res.status !== 404) {
    throw new Error(`Failed to delete message from Thomas (${res.status})`);
  }
}

// ============================================================
// SKIP / GET ITEMS
// ============================================================

export async function skipTriageItem(token, triageItem) {
  await updateItem(token, 'TriageQueue', triageItem.id, { Status: 'skipped' });
  if (triageItem.Source === 'email' && triageItem.EmailMessageId) {
    try {
      await deleteFromThomas(token, triageItem.EmailMessageId);
    } catch (err) {
      console.warn('[Triage] Failed to delete from Thomas on skip (non-critical):', err.message);
    }
  }
}

export async function getPendingTriageItems(token) {
  const all = await getItems(token, 'TriageQueue');
  return all
    .filter(i => i.Status === 'pending' || !i.Status)
    .sort((a, b) => new Date(b.ReceivedDate || 0) - new Date(a.ReceivedDate || 0));
}

export async function getAllTriageItems(token) {
  return getItems(token, 'TriageQueue');
}
